/**
 * 路由表 —— 已实现页走显式路由，其余全部落入 PlaceholderPage。
 *
 * 这样「未实现的导航项」会渲染出说明页（而非 404 白屏或死链），符合
 * docs/03-UIUX.md §4.1「让一个死链看起来像能用，比缺功能更糟」的立场。
 * `/licenses/:id` 是已实列表的下钻，详情页 P-11 已实装（基本信息 + 生命周期时间线 + 设备激活 + Danger Zone）。
 *
 * 登录守卫（P-01）：`/login` 独立于 AppShell；AppShell 内的路由在会话未确认时
 * 渲染整页加载态，确认未登录后由 AuthProvider 的 unauthorized handler 跳 /login。
 */
import { useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { DashboardPage } from './pages/DashboardPage';
import { LicensesPage } from './pages/licenses/LicensesPage';
import { NewLicensePage } from './pages/licenses/NewLicensePage';
import { LicenseDetailPage } from './pages/licenses/LicenseDetailPage';
import { LoginPage } from './pages/LoginPage';
import { PlaceholderPage } from './pages/PlaceholderPage';
import { ProductsPage } from './pages/ProductsPage';
import { ProductDetailPage } from './pages/products/ProductDetailPage';
import { BatchesPage } from './pages/BatchesPage';
import { BatchDetailPage } from './pages/batches/BatchDetailPage';
import { CodesPage } from './pages/CodesPage';
import { CustomersPage } from './pages/CustomersPage';
import { SigningKeysPage } from './pages/SigningKeysPage';
import { IamPage } from './pages/IamPage';
import { AuditPage } from './pages/AuditPage';
import { RedemptionsPage } from './pages/RedemptionsPage';
import { EntitlementsPage } from './pages/EntitlementsPage';
import { SystemPage } from './pages/SystemPage';
import { SecurityPage } from './pages/SecurityPage';
import { PlansPage } from './pages/PlansPage';
import { ActivationsPage } from './pages/ActivationsPage';
import { useAuth } from './auth/AuthContext';

function AppShellGuard() {
  const { currentUser, ready } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (ready && currentUser === null) {
      navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}`, { replace: true });
    }
  }, [ready, currentUser, location.pathname, location.search, navigate]);

  // 会话探测完成前：整页加载态，避免闪现「未登录」再跳走
  if (!ready) {
    return (
      <div className="app-boot" role="status">
        正在确认会话…
      </div>
    );
  }

  // 未登录：渲染等待文案（跳转由 useEffect 触发）
  if (currentUser === null) {
    return (
      <div className="app-boot" role="status">
        正在跳转登录页…
      </div>
    );
  }

  return <AppShell />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<AppShellGuard />}>
        <Route index element={<DashboardPage />} />
        <Route path="licenses" element={<LicensesPage />} />
        <Route path="licenses/new" element={<NewLicensePage />} />
        <Route path="licenses/:id" element={<LicenseDetailPage />} />
        <Route path="products" element={<ProductsPage />} />
        <Route path="products/:id" element={<ProductDetailPage />} />
        <Route path="batches" element={<BatchesPage />} />
        <Route path="batches/:id" element={<BatchDetailPage />} />
        <Route path="codes" element={<CodesPage />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="signing-keys" element={<SigningKeysPage />} />
        <Route path="admins" element={<IamPage />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="redemptions" element={<RedemptionsPage />} />
        <Route path="entitlements" element={<EntitlementsPage />} />
        <Route path="system" element={<SystemPage />} />
        <Route path="security" element={<SecurityPage />} />
        <Route path="plans" element={<PlansPage />} />
        <Route path="activations" element={<ActivationsPage />} />
        <Route path="*" element={<PlaceholderPage />} />
      </Route>
    </Routes>
  );
}
